import { DeliveryAttributes } from 'api-client'
import * as R from 'ramda'

import { COADocumentData } from '~shared-components/components/Card/OrderDetailsCard'

import {
  BatchAttributesWithId,
  OrderListingModel,
  OrderModel,
} from '../model/order.model'
import { ifArrayReturnNthItem } from '../utils/functional'
import { formatDateString } from '../utils/misc'
import { orderLineToPartialBaseCartProduct } from './cart.transforms'
import { transformLocationAttributesWithId } from './location.transforms'

type BatchWithCOADocuments = BatchAttributesWithId & {
  coaDocuments?: COADocumentData[]
}

type DeliveryAttributesWithId = DeliveryAttributes & {
  id: string
}

type BatchWithDeliveries = BatchAttributesWithId &
  BatchWithCOADocuments & {
    delivery: DeliveryAttributesWithId
  }

export const orderResourceToDetailedOrder = (order: TransformedOrder) => {
  const customerContext = order.customerContext
  const sortById = R.sort<TransformedOrder['orderLines'][0]>(
    R.ascend(R.prop('id')),
  )
  const sortedOrderLines = sortById(order.orderLines)

  return {
    customerContextId: customerContext.id,
    id: order.id,
    orders: sortedOrderLines.map(orderLine => ({
      customerProductId: ifArrayReturnNthItem(
        orderLine.product.customerContextProducts,
      )?.number,
      dangerousGoodsClass: orderLine.product.dangerousGoodsClass || [],
      deliveries: order.deliveries
        .map(delivery => ({
          batches: (delivery.batches as BatchWithDeliveries[]).filter(batch =>
            orderLine.batches.some(b => b.id === batch.id),
          ),
          deliveryDate: delivery.deliveryDate,
          formattedDeliveryDate: delivery.formattedDeliveryDate,
          number: delivery.number,
        }))
        .filter(
          delivery =>
            delivery.batches.filter(batch =>
              orderLine.batches.some(b => b.id === batch.id),
            ).length > 0,
        ),
      deliveryInformation: orderLine.deliveryInformation?.map(
        deliveryInformation => ({
          confirmedAmount: deliveryInformation.amountToBeDelivered,
          formattedScheduledDeliveryDate:
            deliveryInformation.formattedScheduledDeliveryDate,
          scheduledDeliveryDate: deliveryInformation.scheduledDeliveryDate,
        }),
      ),
      id: orderLine.id,
      isHazardous: Boolean(
        ifArrayReturnNthItem(orderLine.product.salesGroupProducts)?.hazardous,
      ),
      orderAmount: orderLine.orderAmount,
      orderStatus: orderLine.status,
      packaging: orderLine.product.packaging,
      pricingUnit: orderLine.pricingUnit,
      pricingUnitValue: orderLine.pricingUnitValue,
      product: orderLine.product,
      quantity: orderLine.quantityCalculated,
      reorderData: orderLineToPartialBaseCartProduct(orderLine),
      subtotalValue: orderLine.subtotalValue,
    })),
    status: order.status,
    summary: {
      billToAddress: order.billTo
        ? transformLocationAttributesWithId(order.billTo)
        : undefined,
      customerName: customerContext.customerName,
      customerNumber: customerContext.customerNumber,
      formattedLastUpdateDate: order.formattedLastUpdateDate,
      formattedOrderCreationDate: order.formattedOrderCreationDate,
      formattedRequestedDeliveryDate: order.formattedRequestedDeliveryDate,
      orderCreationDate: order.orderCreationDate,
      paymentTerms: order.paymentTerms,
      purchaseOrderNumber: order.purchaseOrderNumber,
      quotationNumber: order.quotationNumber,
      requestedDeliveryDate: order.requestedDeliveryDate,
      salesOrderNumber: order.salesOrderNumber,
      shipToAddress: order.shipTo
        ? transformLocationAttributesWithId(order.shipTo)
        : undefined,
      shippingInstructions: order.shippingInstructions,
      totalValue: order.totalValue,
      user: order.user?.name,
      userComment: order.commentFromUser,
    },
    timestamp: Date.now(),
  }
}

export const transformOrder = (orderResource: OrderModel) => ({
  ...orderResource,
  deliveries: orderResource.deliveries.map(d => ({
    ...d,
    formattedDeliveryDate: formatDateString(d.deliveryDate),
  })),
  formattedLastUpdateDate: formatDateString(orderResource.lastUpdateDate),
  formattedOrderCreationDate: formatDateString(orderResource.orderCreationDate),
  formattedRequestedDeliveryDate: formatDateString(
    orderResource.requestedDeliveryDate,
  ),
  orderLines: orderResource.orderLines.map(o => ({
    ...o,
    deliveryInformation: o.deliveryInformation?.map(d => ({
      ...d,
      formattedScheduledDeliveryDate: formatDateString(
        d?.scheduledDeliveryDate,
      ),
    })),
  })),
})

export const transformOrders = (resources: OrderListingModel[]) =>
  resources.map(orderResource => ({
    ...orderResource,
    formattedLastUpdateDate: formatDateString(orderResource.lastUpdateDate),
    formattedOrderCreationDate: formatDateString(
      orderResource.orderCreationDate,
    ),
    formattedRequestedDeliveryDate: formatDateString(
      orderResource.requestedDeliveryDate,
    ),
  }))

export type TransformedOrder = ReturnType<typeof transformOrder>
export type TransformedListOrder = ReturnType<typeof transformOrders>[0]
